import React, { useMemo, useRef, useState } from "react";
import "./styleConsentTemplate.css";
import QuestionRenderer from "./QuestionRenderer";
import { ToastAletGlobal } from "../alertDialog/ToastAletGlobal";
import { CanvasComponent } from "../../components/canvas/CanvasComponent";
import { Container, Row, Col, Label } from "reactstrap";



const ConsentTemplate = ({
    template,
    onFinish,
    isSampling
}) => {

    // ? CONFIGURATION ALERT
    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState("info");
    const [toastTitle, setToastTitle] = useState("");
    const [toastMessage, setToastMessage] = useState("");

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});

    // ? CONFIGURATION FIRMS
    const patientCanvasRef = useRef(null);
    const [patientSignature, setPatientSignature] = useState("");


    const closeToastAlert = () => {
        setShowToast(false);
        setToastTitle("");
        setToastMessage("");
    };


    const showToastAlert = (
        type = "info",
        title = "",
        message = ""
    ) => {

        setToastType(type);
        setToastTitle(title);
        setToastMessage(message);
        setShowToast(true);
    };



    const questions = useMemo(() => {

        if (!Array.isArray(template?.questions)) {
            return [];
        }

        return [...template.questions].sort(
            (a, b) =>
                Number(a?.sortOrder || 0) -
                Number(b?.sortOrder || 0)
        );

    }, [template]);


    // ? CONFIGURATION FIRMS QUESTIONS

    const showPatientSignature = template?.patient_signature;

    const hasSignatureStep = showPatientSignature;
    const totalSteps = questions.length + (hasSignatureStep ? 1 : 0);
    const isSignatureStep = hasSignatureStep && currentQuestionIndex === questions.length;
    const isFirstStep = currentQuestionIndex === 0;
    const isLastStep = totalSteps > 0 && currentQuestionIndex === totalSteps - 1;
    const currentQuestion = isSignatureStep ? null : questions[currentQuestionIndex] || null;
    const currentQuestionId = currentQuestion?.id || "";
    const currentAnswer = currentQuestionId ? answers[currentQuestionId] || null : null;



    // * CONFIGURATION SIGNATURE SURVEY
    const handlePatientSignatureChange = (
        canvasRef
    ) => {

        if (
            !canvasRef?.current ||
            typeof canvasRef.current.getDataURL !==
            "function"
        ) {
            return;
        }

        const signatureBase64 =
            canvasRef.current.getDataURL(
                "png",
                false,
                "#FFFFFF"
            );

        setPatientSignature(
            signatureBase64
        );
    };






    const handleClearPatientSignature = () => {

        if (patientCanvasRef.current && typeof patientCanvasRef.current.clear === "function") {

            patientCanvasRef.current.clear();
        }

        setPatientSignature("");
    };




    const hasValidSignature = (
        signatureData
    ) => {
        return (
            typeof signatureData === "string" &&
            signatureData.startsWith("data:image/") &&
            signatureData.includes(";base64,") &&
            signatureData.split(",")[1]?.trim() !== ""
        );
    };


    const isCurrentQuestionAnswered = (

    ) => {

        if (isSignatureStep) {
            return true;
        }

        if (!currentQuestion) {
            return true;
        }

        if (!currentQuestion.required) {
            return true;
        }

        const questionId = currentQuestion.id;
        const answer = answers[questionId];


        switch (currentQuestion.type) {

            case "open":
            case "time":
                return (
                    typeof answer?.value === "string" &&
                    answer.value.trim() !== ""
                );

            case "multiple":
            case "closed":
            case "complex":
                return (
                    Array.isArray(
                        answer?.selectedOptions
                    ) &&
                    answer.selectedOptions.length > 0
                );

            default:
                return true;
        }
    };



    const handlePreviousQuestion = () => {

        if (isFirstStep) {
            return;
        }

        setCurrentQuestionIndex(
            previousIndex =>
                previousIndex - 1
        );
    };


    const handleNextQuestion = (
    ) => {

        if (!isCurrentQuestionAnswered()) {

            showToastAlert(
                "warning",
                "Pregunta obligatoria",
                "Debe responder esta pregunta antes de continuar"
            );

            return;
        }

        if (isLastStep) {
            return;
        }

        setCurrentQuestionIndex(
            previousIndex =>
                previousIndex + 1
        );
    };




    const handleQuestionAnswer = (
        _question,
        _questionAnswer
    ) => {

        const questionId = _question?.id || "";

        if (!questionId) {
            return;
        }

        setAnswers(previousAnswers => ({
            ...previousAnswers,
            [questionId]: _questionAnswer
        }));
    };





    const buildAnswerPayload = (
        _question,
        _answer,
        parentQuestionId = null,
        parentOptionId = null
    ) => {



        if (!_question || !_answer) {
            return null;
        }

        const questionId = String(_question?.id || "");

        const sortedOptions =
            Array.isArray(_question?.options)
                ? [..._question.options].sort(
                    (a, b) =>
                        Number(a?.sortOrder || 0) -
                        Number(b?.sortOrder || 0)
                )
                : [];


        const selectedOptionIds =
            Array.isArray(_answer?.selectedOptions)
                ? _answer.selectedOptions.map(String)
                : [];


        const selectedOptions =
            selectedOptionIds
                .map((selectedOptionId) => {

                    const option = sortedOptions.find(
                        currentOption =>
                            String(currentOption?.id || "") ===
                            selectedOptionId
                    );

                    if (!option) {
                        return null;
                    }

                    return Number(option.id);
                })
                .filter(option => option !== null);


        return {
            id_question: questionId,
            question: _question?.question || "",
            type: _question?.type || "",

            value:
                _answer?.value !== undefined &&
                    _answer?.value !== null
                    ? _answer.value
                    : null,

            parent_question_id:
                parentQuestionId
                    ? String(parentQuestionId)
                    : null,

            parent_option_id:
                parentOptionId
                    ? String(parentOptionId)
                    : null,

            selected_options: selectedOptions
        };
    };


    const buildQuestionAnswersPayload = (
        question,
        answer,
        parentQuestionId = null,
        parentOptionId = null
    ) => {

        const results = [];

        if (!question || !answer) {
            return results;
        }


        const currentAnswer = buildAnswerPayload(
            question,
            answer,
            parentQuestionId,
            parentOptionId
        );

        if (currentAnswer) {
            results.push(currentAnswer);
        }


        const selectedOptionIds = Array.isArray(answer?.selectedOptions) ? answer.selectedOptions.map(String) : [];


        const subAnswers = answer?.subAnswers || {};


        const options = Array.isArray(question?.options) ? question.options : [];


        selectedOptionIds.forEach(selectedOptionId => {

            const selectedOption = options.find(
                option =>
                    String(option?.id || "") ===
                    selectedOptionId
            );


            if (
                !selectedOption ||
                !selectedOption.hasSubQuestion ||
                !selectedOption.subQuestion
            ) {
                return;
            }


            const subQuestion =
                selectedOption.subQuestion;

            const subAnswer =
                subAnswers?.[selectedOptionId] ||
                subAnswers?.[String(subQuestion.id)];


            console.log(
                "🚀 SUBQUESTION:",
                subQuestion
            );

            console.log(
                "🚀 SUBANSWER:",
                subAnswer
            );


            if (!subAnswer) {
                return;
            }


            const childAnswers =
                buildQuestionAnswersPayload(
                    subQuestion,
                    subAnswer,
                    question.id,
                    selectedOption.id
                );


            results.push(...childAnswers);
        });


        return results;
    };



    // ? FINISH QUESTIONS
    const handleFinishSurvey = () => {

        if (
            !isSignatureStep && !isCurrentQuestionAnswered()
        ) {

            showToastAlert(
                "warning",
                "Pregunta obligatoria",
                "Debe responder esta pregunta antes de finalizar"
            );

            return;
        }


        if (
            showPatientSignature && !hasValidSignature(patientSignature)
        ) {

            showToastAlert(
                "warning",
                "Firma requerida",
                "Debe capturar la firma del paciente"
            );

            return;
        }

        const formattedAnswers = questions
            .flatMap(question => {

                const answer =
                    answers?.[String(question.id)] ||
                    answers?.[question.id];

                return buildQuestionAnswersPayload(
                    question,
                    answer
                );
            });
        console.log("🚀 ~ handleFinishSurvey ~ formattedAnswers:", formattedAnswers)


        const result = {
            id_template: template?.id_template,
            title: template?.title || "",
            version: template?.version || "",
            answers: formattedAnswers,
            patient_signature: showPatientSignature ? patientSignature : null,
        };

        console.log("🚀 ~ handleFinishSurvey ~ result:", result)


        if (typeof onFinish === "function") {
            onFinish(result);
        }

    };

    if (!template) {

        return (
            <div className="survey-runner-empty">
                No se encontró la plantilla.
            </div>
        );
    }


    if (
        questions.length === 0 && !hasSignatureStep
    ) {

        return (
            <div className="survey-runner-empty">
                Esta plantilla no contiene preguntas.
            </div>
        );
    }



    return (
        <div className="survey-runner">

            <div className="survey-runner-card">
                <Col sm="12">
                    <Row>
                        <Col sm="1" className="pt-4 pl-0 pr-0" >
                            <div className="survey-runner-navigation">

                                <button
                                    type="button"
                                    className="survey-runner-back-button"
                                    onClick={handlePreviousQuestion}
                                    disabled={isFirstStep}
                                >
                                    <i className="fa fa-arrow-left">
                                    </i>
                                </button>


                            </div>
                        </Col>
                        <Col sm="10">
                            <div className="survey-runner-title">
                                {template?.title || "Formulario de consentimiento"}
                            </div>
                            <div className="survey-runner-title-line">
                            </div>

                        </Col>
                        <Col sm="1" className="pt-4 pl-0" >

                            <div className="survey-runner-counter">

                                {currentQuestionIndex + 1}

                                {" / "}

                                {totalSteps}

                            </div>


                        </Col>
                    </Row>
                </Col>








                <div className="survey-runner-question">

                    {!isSignatureStep ? (

                        <>
                            <div className="survey-runner-question-text">
                                <Col sm="12" className="pl-0 pr-0"  >
                                    <Row>
                                        <Col sm="10">
                                            {currentQuestion?.question}

                                            {currentQuestion?.required && (
                                                <span className="survey-runner-required">
                                                    *
                                                </span>
                                            )}


                                        </Col>
                                        <Col sm="2">
                                            {!isLastStep ? (

                                                <button
                                                    type="button"
                                                    className="survey-runner-next-button"
                                                    onClick={handleNextQuestion}
                                                >
                                                    {hasSignatureStep &&
                                                        currentQuestionIndex ===
                                                        questions.length - 1
                                                        ? "Firmas"
                                                        : "Siguiente"
                                                    }

                                                    <i className="fa fa-arrow-right">
                                                    </i>
                                                </button>

                                            ) : null

                                            }


                                        </Col>
                                    </Row>
                                </Col>




                            </div>


                            <div className="survey-runner-question-content">

                                <QuestionRenderer
                                    question={
                                        currentQuestion
                                    }
                                    answer={
                                        currentAnswer
                                    }
                                    onChange={(
                                        questionAnswer
                                    ) =>
                                        handleQuestionAnswer(
                                            currentQuestion,
                                            questionAnswer
                                        )
                                    }
                                />





                            </div>
                        </>

                    ) : (

                        <div className="survey-runner-signature-step">

                            <div className="survey-runner-signature-list">

                                {showPatientSignature && (

                                    <div className="survey-runner-signature-card">

                                        <div className="survey-runner-signature-card-header">

                                            <div>
                                                <strong>
                                                    Firma del paciente
                                                </strong>

                                                <span>
                                                    Firme dentro del recuadro
                                                </span>
                                            </div>


                                            <button
                                                type="button"
                                                className="survey-runner-signature-clear-button"
                                                onClick={
                                                    handleClearPatientSignature
                                                }
                                            >
                                                <i className="fa fa-eraser">
                                                </i>

                                                Limpiar
                                            </button>

                                        </div>


                                        <div className="survey-runner-signature-canvas">

                                            <CanvasComponent
                                                canvasRef={
                                                    patientCanvasRef
                                                }
                                                handleChange={
                                                    handlePatientSignatureChange
                                                }
                                                width={420}
                                                height={180}
                                                brushColor="#1e293b"
                                                brushRadius={2}
                                                lazyRadius={0}
                                                previousImage=""
                                            />

                                        </div>

                                    </div>
                                )}

                            </div>

                        </div>
                    )}

                </div>


                <div className="survey-runner-footer">

                    {!isLastStep ? null : (

                        <button
                            type="button"
                            className="survey-runner-finish-button"
                            onClick={handleFinishSurvey}
                        >
                            Finalizar formulario
                        </button>
                    )}

                </div>
                {
                    //  * MESSAGE
                }

                <Col className={isSampling ? "d-none" : "pb-2"}   >
                    {
                        currentQuestion?.gender === "female" && (
                            <span className="survey-runner f-12 " style={{ color: "#fd5d00", fontWeight: "bold" }}   >
                                &nbsp;<i className="fa fa-exclamation-triangle"></i>  Esta pregunta solo saldra si es Mujer
                            </span>
                        )
                    }
                    {
                        currentQuestion?.gender === "male" && (
                            <span className="survey-runner f-12 " style={{ color: "#fd5d00", fontWeight: "bold" }}   >
                                &nbsp;<i className="fa fa-exclamation-triangle"></i>  Esta pregunta solo saldra si es Hombre
                            </span>
                        )
                    }
                </Col>


            </div>


            <ToastAletGlobal
                visible={showToast}
                type={toastType}
                title={toastTitle}
                message={toastMessage}
                onClose={closeToastAlert}
            />

        </div>
    );
};


export default ConsentTemplate;






